import { createContext, ReactNode, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { get, isObject } from 'lodash';
import { useDebounceValue } from 'usehooks-ts';

import { normalizePath } from 'services/webedi/webedi';

// Type definitions
type FieldArrayActions = Pick<ReturnType<typeof useFieldArray>, 'fields' | 'append' | 'remove'>;

type CustomSetValue = {
  setValue: (
    name: string | undefined,
    value: any,
    options?: Parameters<ReturnType<typeof useFormContext>['setValue']>[2],
  ) => void;
};

type RemoveEmbedded = {
  removeEmbedded: (rowIndex: number, embeddedRowIndex: number, columnGroupName: string) => void;
};

type AddEmbedded = {
  addEmbedded: (rowIndex: number, columnGroupName: string) => void;
};

type FormUtilities = Pick<ReturnType<typeof useFormContext>, 'register' | 'resetField'>;

type DataTableContextValue = FieldArrayActions &
  CustomSetValue &
  RemoveEmbedded &
  AddEmbedded &
  FormUtilities & {
    baseData?: Array<unknown>;
    revisedData?: Array<unknown>;
    // Increments on an external whole-array reshape; used as a remount key for the table body.
    reshapeKey: number;
  };

// Everything in the context except the row snapshot. `fields` changes on every add, remove and
// (debounced) edit, so a consumer that only needs to call these — a cell's `setValue`, a delete
// button's `remove` — would otherwise re-render each time for nothing.
type DataTableActionsValue = Omit<DataTableContextValue, 'fields'>;

// Context creation
const DataTableContext = createContext<DataTableContextValue | null>(null);
const DataTableActionsContext = createContext<DataTableActionsValue | null>(null);

const EMPTY_ARRAY: Array<any> = [];
const DEBOUNCE_DELAY = process.env.NODE_ENV === 'test' ? 50 : 500;

/**
 * Higher-Order Component that wraps a component with DataTableProvider
 * @param Component The component to wrap
 * @param dataPrefix The prefix to use for the data table
 * @returns A wrapped component that has access to DataTableContext
 */
export function withDataTableProvider<T extends object>(Component: React.FC<T>, dataPrefix: string) {
  // eslint-disable-next-line react/display-name
  return (props: T) => {
    return (
      <DataTableProvider name={dataPrefix}>
        <Component {...props} />
      </DataTableProvider>
    );
  };
}

// DataTableProvider component
export function DataTableProvider({
  name,
  children,
  baseData,
  revisedData,
  cheapDirtyCheck = false,
}: {
  name: string;
  children: ReactNode;
  baseData?: Array<unknown>;
  revisedData?: Array<unknown>;
  /**
   * `append`/`remove` write the whole array with `shouldDirty`, and React Hook Form then compares
   * the entire form with its defaults once per field it wrote — cost grows with the square of the
   * rows, and is at its worst (a full comparison, not one that stops at the first difference) when
   * the write puts the form back to what it started as, e.g. removing the row that was just added.
   * With this, the array is written without it and the form's dirtiness is worked out once, by
   * touching a single field. Only `isDirty` is then accurate, not `dirtyFields` for the written rows.
   */
  cheapDirtyCheck?: boolean;
}) {
  const {
    setValue,
    register,
    resetField,
    trigger,
    getValues,
    control,
    formState: { isSubmitted },
  } = useFormContext();

  const mode = control._options?.mode;
  const revalidateMode = control._options?.reValidateMode;
  const shouldTriggerValidation = mode !== 'onSubmit' && revalidateMode !== 'onSubmit';

  const { syncedFields, setSyncedFields, reshapeKey } = useSynchronizedFields(name);
  // Custom setValue function to handle nested fields
  const handleSetValue: CustomSetValue['setValue'] = useCallback(
    (fieldName, value, options) => {
      if (!fieldName) {
        setValue(name, value, options);
      } else {
        const nestedField = fieldName.replace(`${name}.`, '');
        setValue(`${name}.${nestedField}`, value, options);
      }
      if (isSubmitted && shouldTriggerValidation && options?.shouldValidate !== false) {
        trigger();
      }
    },
    [name, setValue, trigger, isSubmitted, shouldTriggerValidation],
  );

  /**
   * Custom append function to handle the sync between field array and the values
   * Should not be called multiple times in a row, as it will not update the values correctly (syncedFields will be outdated)
   * TODO: Fix above issue
   */
  const handleAppend: ReturnType<typeof useFieldArray>['append'] = useCallback(
    (value) => {
      const currentValues = getValues(name) || [];
      const formattedValue = [value].flat();
      const newValues = [...currentValues, ...formattedValue];

      setSyncedFields(newValues);
      writeRows(setValue, name, newValues, cheapDirtyCheck);

      if (isSubmitted && shouldTriggerValidation) {
        trigger();
      }
    },
    [getValues, name, isSubmitted, shouldTriggerValidation, setSyncedFields, setValue, trigger, cheapDirtyCheck],
  );

  /**
   * Custom remove function to handle the sync between field array and the values
   * Should not be called multiple times in a row, as it will not update the values correctly (syncedFields will be outdated)
   * TODO: Fix above issue
   */
  const handleRemove: ReturnType<typeof useFieldArray>['remove'] = useCallback(
    (args) => {
      const currentValues = getValues(name) as Array<any>;
      if (typeof args === 'number') {
        const newValue = currentValues.toSpliced(args, 1);
        writeRows(setValue, name, newValue, cheapDirtyCheck);
        setSyncedFields(newValue);
      } else {
        throw new Error('DataTableContext multiple removal is not yet supported');
      }

      if (isSubmitted && shouldTriggerValidation) {
        trigger();
      }
    },
    [getValues, name, isSubmitted, shouldTriggerValidation, setValue, setSyncedFields, trigger, cheapDirtyCheck],
  );

  const handleRemoveEmbedded: RemoveEmbedded['removeEmbedded'] = useCallback(
    (rowIndex: number, embeddedRowIndex: number, columnGroupName: string) => {
      const currentValues = getValues(name) as Array<any>;
      const currentRowValue = currentValues[rowIndex];
      const pathToItems = normalizePath(columnGroupName);
      const currentEmbeddedItems = get(currentRowValue, pathToItems);

      if (!Array.isArray(currentEmbeddedItems)) {
        throw new Error(`Field at path ${columnGroupName} is not an array`);
      }

      const updatedItems = [
        ...currentEmbeddedItems.slice(0, embeddedRowIndex),
        ...currentEmbeddedItems.slice(embeddedRowIndex + 1),
      ];
      const updatedRow = {
        ...currentRowValue,
        [columnGroupName]: updatedItems,
      };

      const updatedValues = [...currentValues];
      updatedValues[rowIndex] = updatedRow;

      // When removing an array item, indices shift (e.g., item at index 2 becomes index 1).
      // React Hook Form maintains internal field registrations by path (e.g., 'data.0.discounts.2').
      // Without resetField, RHF keeps registrations for old paths, causing misaligned validation state
      // and stale field metadata. resetField clears all registrations under this path and forces
      // fields to re-register with correct indices on next render.
      resetField(`${name}.${rowIndex}.${columnGroupName}`, { defaultValue: updatedItems });
      setValue(`${name}.${rowIndex}`, updatedRow, { shouldDirty: true });
      setSyncedFields(updatedValues);

      if (isSubmitted && shouldTriggerValidation) {
        trigger();
      }
    },
    [getValues, name, resetField, setSyncedFields, setValue, isSubmitted, shouldTriggerValidation, trigger],
  );

  const handleAddEmbedded: AddEmbedded['addEmbedded'] = useCallback(
    (rowIndex: number, columnGroupName: string) => {
      const currentValues = getValues(name) as Array<any>;
      const currentRowValue = currentValues[rowIndex];
      const pathToItems = normalizePath(columnGroupName);
      const currentEmbeddedItems = get(currentRowValue, pathToItems);

      if (!Array.isArray(currentEmbeddedItems) && currentEmbeddedItems) {
        throw new Error(`Field at path ${columnGroupName} is not an array`);
      }

      const updatedItems = [...(currentEmbeddedItems || []), {}];
      const updatedRow = {
        ...currentRowValue,
        [columnGroupName]: updatedItems,
      };

      const updatedValues = [...currentValues];
      updatedValues[rowIndex] = updatedRow;

      // When adding an array item, React Hook Form needs to register new fields at the new indices.
      // resetField clears existing registrations and ensures all fields re-register cleanly with
      // the expanded array structure, preventing stale field state from persisting.
      resetField(`${name}.${rowIndex}.${columnGroupName}`, { defaultValue: updatedItems });
      setValue(`${name}.${rowIndex}`, updatedRow, { shouldDirty: true });
      setSyncedFields(updatedValues);

      if (isSubmitted && shouldTriggerValidation) {
        trigger();
      }
    },
    [getValues, name, resetField, setValue, setSyncedFields, isSubmitted, shouldTriggerValidation, trigger],
  );

  const handleRegister: typeof register = useCallback(
    (...args) => {
      const registerResult = register(...args);
      return {
        ...registerResult,
        onChange: (e) => {
          return new Promise((resolve, reject) => {
            registerResult
              .onChange(e)
              .then(() => {
                if (isSubmitted && shouldTriggerValidation) {
                  trigger();
                }
                resolve();
              })
              .catch(reject);
          });
        },
      };
    },
    [register, isSubmitted, shouldTriggerValidation, trigger],
  );

  // Memoized context values
  const actionsValue = useMemo<DataTableActionsValue>(
    () => ({
      append: handleAppend,
      remove: handleRemove,
      removeEmbedded: handleRemoveEmbedded,
      addEmbedded: handleAddEmbedded,
      setValue: handleSetValue,
      register: handleRegister,
      resetField,
      baseData,
      revisedData,
      reshapeKey,
    }),
    [
      handleAppend,
      handleRemove,
      handleRemoveEmbedded,
      handleAddEmbedded,
      handleSetValue,
      handleRegister,
      resetField,
      baseData,
      revisedData,
      reshapeKey,
    ],
  );
  const contextValue = useMemo<DataTableContextValue>(
    () => ({ ...actionsValue, fields: syncedFields }),
    [actionsValue, syncedFields],
  );

  return (
    <DataTableActionsContext.Provider value={actionsValue}>
      <DataTableContext.Provider value={contextValue}>{children}</DataTableContext.Provider>
    </DataTableActionsContext.Provider>
  );
}

// Custom hook to use the DataTableContext
export function useDataTableContext() {
  const context = useContext(DataTableContext);

  if (!context) {
    throw new Error('useDataTableContext must be used within a DataTableProvider');
  }
  return context;
}

/**
 * Like `useDataTableContext`, without `fields`: the consumer does not re-render when the rows
 * change. For components that only call the actions and read their own values elsewhere (e.g.
 * `useWatch` on their field) — the ones that sit in every row.
 */
export function useDataTableActions() {
  const context = useContext(DataTableActionsContext);

  if (!context) {
    throw new Error('useDataTableActions must be used within a DataTableProvider');
  }
  return context;
}

// Custom hook for field synchronization
// We want to have a debounced version of the fields to avoid unnecessary re-renders
// But when we append we want that to be reflected in the fields immediately
// So we have a syncedFields state that is updated from debouncedFields
// We use setSyncedFields to be called when we append something so the user can see it happening right away

function useSynchronizedFields(name: string) {
  const fields = useWatch({ name });
  const [debouncedFields, setDebouncedFields] = useDebounceValue(fields, DEBOUNCE_DELAY);
  const [syncedFields, setSyncedFields] = useState(fields);
  // Bumped on an external reshape (below) to force a fresh remount of the table body.
  const [reshapeKey, setReshapeKey] = useState(0);

  // Effect to update debouncedFields
  useLayoutEffect(() => {
    setDebouncedFields(fields || EMPTY_ARRAY);
  }, [fields, setDebouncedFields]);

  // Effect to update syncedFields from debouncedFields
  useLayoutEffect(() => {
    setSyncedFields(debouncedFields || EMPTY_ARRAY);
  }, [debouncedFields]);

  // An EXTERNAL reshape — one that rewrites the whole array through the raw form API instead of the
  // append/remove/embedded helpers below (e.g. the packing-list "Apply") — changes the row count
  // without keeping syncedFields in step. Left to the debounce, the table keeps rendering the old row
  // count for ~500ms; the removed rows' inputs stay mounted, re-register, and read their stale DOM
  // value back into the form, so the merged-away rows never leave and their values bleed into the
  // surviving rows (see docs/decisions/webedi.md). Adjusting the state DURING render —
  // rather than in an effect — makes React discard this pass and re-render before committing, so the
  // removed inputs never commit (nothing to read back) and, via the bumped remount key, the survivors
  // mount fresh from the new array. Append/remove sync syncedFields themselves and value-only edits
  // keep the same length, so neither trips this branch.
  if ((fields || EMPTY_ARRAY).length !== (syncedFields || EMPTY_ARRAY).length) {
    setSyncedFields(fields || EMPTY_ARRAY);
    setReshapeKey((key) => key + 1);
  }

  return { syncedFields: syncedFields || EMPTY_ARRAY, setSyncedFields, reshapeKey };
}

/** Writes the rows of the table's array to the form, as dirty. See `cheapDirtyCheck`. */
function writeRows(
  setValue: ReturnType<typeof useFormContext>['setValue'],
  name: string,
  rows: Array<any>,
  cheapDirtyCheck: boolean,
) {
  const firstRow = rows[0];
  const probeKey =
    cheapDirtyCheck && firstRow ? Object.keys(firstRow).find((key) => !isObject(firstRow[key])) : undefined;

  if (probeKey === undefined) {
    setValue(name, rows, { shouldDirty: true });
    return;
  }

  setValue(name, rows, { shouldDirty: false });
  setValue(`${name}.0.${probeKey}`, firstRow[probeKey], { shouldDirty: true });
}
